import React from 'react';
import { ReactComponent as ArrowLeft } from '@/img/pagination-arrow-left.svg';
import { ReactComponent as ArrowRight } from '@/img/pagination-arrow-right.svg';
import './UIPaginator.scss';

export interface UIPaginatorProps {
  /**
   * Поточна сторінка
   */
  currentPage?: number;

  /**
   * Загальна кількість сторінок
   */
  totalPages?: number;

  /**
   * Обробник зміни сторінки
   */
  onPageChange?: (page: number) => void;

  /**
   * Текст для відображення інформації про сторінки
   */
  pageInfo?: string;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UIPaginator';

const UIPaginator: React.FC<UIPaginatorProps> = ({
  currentPage: rawCurrentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
  pageInfo,
  className = '',
}) => {
  const currentPage = Number(rawCurrentPage);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const containerClasses = [baseClass, className].filter(Boolean).join(' ');

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, currentPage + 2);

      if (start > 1) {
        pages.push(1);
        if (start > 2) {
          pages.push('...');
        }
      }

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push('...');
        }
        if (!pages.includes(totalPages)) {
          pages.push(totalPages);
        }
      }
    }

    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className={containerClasses}>
      <button
        className={`${baseClass}__arrow ${baseClass}__arrow--prev`}
        onClick={handlePrevPage}
        disabled={currentPage <= 1}
      >
        <ArrowLeft />
      </button>

      <div className={`${baseClass}__pages`}>
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={index} className={`${baseClass}__dots`}>
              ...
            </span>
          ) : (
            <button
              key={index}
              className={`${baseClass}__page ${
                page === currentPage ? `${baseClass}__page--active` : ''
              }`}
              onClick={() => onPageChange(page as number)}
            >
              {page}
            </button>
          )
        ))}
      </div>

      <button
        className={`${baseClass}__arrow ${baseClass}__arrow--next`}
        onClick={handleNextPage}
        disabled={currentPage >= totalPages}
      >
        <ArrowRight />
      </button>
    </div>
  );
};

export default UIPaginator;
